import { Spin } from 'antd'
import autobind from 'autobind-decorator'
import * as React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Link, RouteComponentProps, withRouter } from 'react-router-dom'
import { connect } from 'redux-scaffolding-ts'
import { IdentityProps, withIdentity } from 'src/core/services/authentication'
import { formatMessage } from 'src/core/services/http.service'
import { ItemState, Query } from 'src/core/stores/dataStore'
import { CommandResult } from 'src/core/stores/types'
import { TableModel, TableView } from 'src/core/ui/collections/table'
import { RestrictionPlansStore, RestrictionPlanSummary } from 'src/stores/restriction-plans-store'
import NewRestrictionPlanView from './restriction-plan-new'
import EditableTooltip from '../../../components/editable-tooltip/tooltip'
import { CacheProps, withCache } from '../../../core/services/cache.service';
import {UserProfileProps, withUserProfile} from "../../../components/user-profile";
import {isReadOnly} from "../../../utils/CheckValidationbyUsers";

interface RestrictionPlansViewProps
	extends RouteComponentProps<{ companyId: string }>, CacheProps,
		WithTranslation,
		UserProfileProps {
	companyId: string
	companyName: string
	onDelete: () => void
}

interface RestrictionPlansViewState {
	query: Query
	isBusy: boolean
	creationModalIsOpen: boolean
}

@connect(['restrictionPlans', RestrictionPlansStore])
class RestrictionPlansDashboard extends React.Component<
	RestrictionPlansViewProps,
	RestrictionPlansViewState
	> {
	private get restrictionPlansStore() {
		return (this.props as any).restrictionPlans as RestrictionPlansStore
	}

	constructor(props: RestrictionPlansViewProps) {
		super(props)

		var queryStored = props.cache.getWithCustomKey("Restriction-Plan",true)

		var query = {
			searchQuery: '',
			orderBy: [
				{
					field: 'displayName',
					direction: 'Ascending',
					useProfile: true
				}
			],
			skip: 0,
			take: 10
		}

		if (queryStored)
			query = queryStored

		this.state = {
			query: query as any,
			isBusy: false,
			creationModalIsOpen: false
		}
	}

	componentWillMount() {
		this.restrictionPlansStore.changeUrl(this.props.companyId)
		if (!this.props.cache.isInCache("Restriction-Plan") || this.props.cache.isNavigationBackwards())
			this.load(this.state.query)
	}

	@autobind
	private load(query: Query) {
		this.props.cache.saveWithCustomKey("Restriction-Plan", query);
		return this.restrictionPlansStore.getAllAsync(query)
	}

	@autobind
	private onQueryChanged(query: Query) {
		this.setState({ query }, () => this.load(query))
	}

	@autobind
	private async onSaveItem(item: RestrictionPlanSummary, state: ItemState) {
		var result = await this.restrictionPlansStore.saveAsync(`${item.id}`, item, state)
		await this.load(this.state.query)
		return result
	}

	@autobind
	private async onDeleteRow(item: RestrictionPlanSummary, state: ItemState): Promise<CommandResult<any>> {
		const result = await this.restrictionPlansStore.deleteAsync(`${item.id}`);
		this.props.onDelete && this.props.onDelete();
		return result;
	}

	@autobind
	private async onCreationModalOpen() {
		this.setState({ creationModalIsOpen: true })
	}

	@autobind
	private async onCreationModalClose(id: string | undefined) {
		this.setState({ creationModalIsOpen: false })
		this.load(this.state.query)
	}

	public render() {
		const { t } = this.props as any
		const tableModel = {
			query: this.state.query,
			columns: [
				{
					sortable: true,
					field: 'displayName',
					title: t('Regime Name'),
					renderer: (data: RestrictionPlanSummary) => (
						<span>
							<Link
								to={{
									pathname: `/time-tracking/admin/companies/${
										this.props.match.params.companyId
									}/restrictionplans/${data.id}`,
									state: {
										titles: [
											...(this.props.location.state
												? (this.props.location.state as any).titles || []
												: []),
											{
												url: `/time-tracking/admin/companies/${
													this.props.match.params.companyId
												}`,
												title: this.props.companyName
											}
										]
									}
								}}>
								{data.displayName}
							</Link>
						</span>
					)
				},
				{
					sortable: false,
					align: "center",
					title: <EditableTooltip name="restriction-plan-list-hours"> {t("Weekly Hours")}</EditableTooltip>,
					renderer: (data) => (
						<span> {data.weekMinHours} / {data.weekMaxHours} </span>
					)
				}
			],
			data: this.restrictionPlansStore.state,
			sortFields: [
				{
					field: 'displayName',
					text: t('Name'),
					useProfile: true
				}
			]
		} as TableModel<RestrictionPlanSummary>

		return (
			<Spin
				size="large"
				spinning={this.restrictionPlansStore.state.isBusy || this.state.isBusy}>
				<TableView
					showDeleteStatusWindow
					rowKey="id"
					model={tableModel}
					error={
						this.restrictionPlansStore.state.result &&
						!this.restrictionPlansStore.state.result.isSuccess &&
						formatMessage(this.restrictionPlansStore.state.result)
					}
					embedded={false}
					onQueryChanged={(q: Query) => this.onQueryChanged(q)}
					onRefresh={() => this.load(this.state.query)}
					canEdit={false}
					canDelete={!isReadOnly(this.props.userProfile)}
					canCreateNew={!isReadOnly(this.props.userProfile)}
					onNewItem={this.onCreationModalOpen}
					onDeleteRow={this.onDeleteRow}
				/>
				{this.state.creationModalIsOpen && (
					<NewRestrictionPlanView
						userProfile={this.props.userProfile}
						companyId={this.props.match.params.companyId}
						onClose={this.onCreationModalClose}
					/>
				)}
			</Spin>
		)
	}
}

// Wire up the React component to the Redux store
export default withUserProfile(withCache( withTranslation()(withIdentity(withRouter(RestrictionPlansDashboard)))))
